import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IDifficultyLevels } from '@core/interfaces/idifficulty-levels';
import { LookupService } from '@core/services/lookup.service';
import { ITeacherSurvey, ITeacherSurveyRequest } from '../../interfaces/iteacher-survey';
import { take, tap } from 'rxjs';
import { RatingService } from '@core/services/rating.service';
import { ToastService } from '@core/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-teacher-survey-modal',
    templateUrl: './teacher-survey-modal.component.html',
    styleUrls: ['./teacher-survey-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeacherSurveyModalComponent implements OnInit {
  difficultyLevels: IDifficultyLevels[] = [];
  difficultyForm!: FormGroup;

  public get id() {
    return this.difficultyForm.get('id');
  }

  public get difficultyLevelId() {
    return this.difficultyForm.get('difficultyLevelId');
  }

  public get difficultyAnswer() {
    return this.difficultyForm.get('difficultyAnswer');
  }

  public get extentLevelId() {
    return this.difficultyForm.get('extentLevelId');
  }
  constructor(public dialog: MatDialog, 
              private lookupService: LookupService, 
              private ratingService: RatingService,
              private toast: ToastService,
              private fb: FormBuilder, 
              public dialogRef: MatDialogRef<TeacherSurveyModalComponent>,
              @Inject(MAT_DIALOG_DATA) public dialogData: any,
              private translocoService: TranslocoService) {}
 
  ngOnInit(): void {
    this.initializeForm();
    this.getDifficultyLevels();
  }

  getDifficultyLevels() { 
    this.lookupService.getDifficultyLevels().pipe(
      take(1),
      tap((data: IDifficultyLevels[]) => {
        this.difficultyLevels = data;
      })
    ).subscribe();
  }

  getTeacherSurvey() {
    const { cardId, measureId } = this.dialogData.teacherSurveyReq ?? this.dialogData;
    if (cardId && measureId) {
      this.ratingService.getTeacherSurvey(cardId, measureId)
        .pipe(
          take(1),
          tap((data: ITeacherSurvey) => {
            if (data) {
              this.id?.setValue(data.id);
              this.difficultyLevelId?.setValue(data.difficultyLevelId);
              this.difficultyAnswer?.setValue(data.difficultyAnswer);
              this.extentLevelId?.setValue(data.extentLevelId);
            }
          })
        )
        .subscribe();
    }
  }

  initializeForm() {
    const { cardId, measureId } = this.dialogData.teacherSurveyReq ?? this.dialogData;
    this.difficultyForm = this.fb.group({
      id: [0],
      carId: [cardId],
      measureId: [measureId],
      difficultyLevelId: [null, Validators.required],
      difficultyAnswer: [null],
      extentLevelId: [null],
    });

    if (this.dialogData.teacherSurveyReq?.isStructuredPrompt || this.dialogData?.isStructuredPrompt) {
      this.difficultyForm.get('extentLevelId')?.setValidators(Validators.required);
    } else {
      this.difficultyForm.get('extentLevelId')?.clearValidators();
    }
    this.difficultyForm.get('extentLevelId')?.updateValueAndValidity();

    if (this.dialogData.viewOnly) {
      this.difficultyLevelId?.disable();
      this.difficultyAnswer?.disable();
    }
    
    this.getTeacherSurvey();
  }

  isPTKMathAndNotDefault(level: any): boolean {
    return !level.isDefault && (this.dialogData.teacherSurveyReq?.isStructuredPrompt || this.dialogData?.isStructuredPrompt);
  }

  onSubmit() {
    if (this.difficultyForm.invalid) {
      this.toast.error(this.translocoService.translate('user.inputRating.teacherSurvey.selectionRequiredToast'));
      return;
    }

    if (this.difficultyAnswer?.value && this.difficultyAnswer.value.length > 2000) {
      this.toast.error(this.translocoService.translate('user.inputRating.teacherSurvey.maxLengthToast'));
      return;
    }

    const payload: ITeacherSurvey = {
      ...this.difficultyForm.value
    }
    this.dialogRef.close({ payload: payload });
  }

}
